import { useEffect, useMemo, useState } from "react";
import { Button, ContentCard, Dialog, LoadingSkeleton, Row, Toggle } from "@narmi/design_system";
import { useNotificationContext } from "cerulean/src/NotificationContext";
import { $staffApi, queryClient } from "../../api";

const PRODUCTS = {
  personal_banking: "Consumer banking",
  business_banking: "Business banking",
  online_banking: "Online banking",
  mobile_banking: "Mobile banking",
  new_account_opening: "Consumer account opening",
  business_account_opening: "Business account opening",
  staffer_led_ao_consumer: "Staff-led consumer account opening",
  staffer_led_ao_business: "Staff-led business account opening",
  aoiob: "Account opening in online banking",
  lending: "Lending",
} as const;

type ProductKey = keyof typeof PRODUCTS;
type ProductLabel = (typeof PRODUCTS)[ProductKey];
type ProductState = Record<ProductKey, boolean>;

const PRODUCT_KEYS = Object.keys(PRODUCTS) as ProductKey[];

function ToggleRow({
  label,
  isActive,
  onChange,
  disabled,
  helperText,
  paddingSize,
}: {
  label: ProductLabel;
  isActive: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  helperText?: string;
  paddingSize?: "none" | "xxs" | "xs" | "s" | "m";
}) {
  const labelId = `toggle-${label.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <div className={paddingSize ? `padding--bottom--${paddingSize}` : ""}>
      <label id={labelId}>
        <Row alignItems="center">
          <Row.Item>
            <span className="fontSize--s fontWeight--semibold">{label}</span>
          </Row.Item>
          <Row.Item shrink>
            <Toggle
              isActive={isActive}
              onChange={onChange}
              labelledBy={labelId}
              disabled={disabled}
            />
          </Row.Item>
        </Row>
      </label>
      {helperText && (
        <p
          style={{ width: "75%", marginTop: "2px" }}
          className="fontSize--xs fontColor--secondary  margin--bottom--none"
        >
          {helperText}
        </p>
      )}
    </div>
  );
}

function EditProductSectionHeader({ icon, title }: { icon: string; title: string }) {
  return (
    <div className="margin--bottom--xxs">
      <Row alignItems="center" gapSize="xxs">
        <Row.Item shrink>
          <i className={`narmi-icon-${icon} fontSize--s`} />
        </Row.Item>
        <Row.Item>
          <div className="fontSize--xs fontWeight--bold fontColor--secondary">{title}</div>
        </Row.Item>
      </Row>
    </div>
  );
}

export function EditProductModal({
  open,
  onSuccess,
  onExit,
}: {
  open: boolean;
  onSuccess: () => void;
  onExit: () => void;
}) {
  const { sendNotification } = useNotificationContext();

  const { data, isLoading } = $staffApi.useQuery(
    "get",
    "/api/institution_settings/{setting_name}/",
    { params: { path: { setting_name: "institution_features" } } },
    { enabled: open }
  );

  const initialState: ProductState | null = useMemo(() => {
    if (!data) return null;
    const value = typeof data.value === "string" ? JSON.parse(data.value) : data.value;
    const state: ProductState = {} as ProductState;
    for (const key of PRODUCT_KEYS) {
      state[key] = key in value ? Boolean(value[key]) : false;
    }
    return state;
  }, [data]);

  const [formData, setFormData] = useState<ProductState | null>(null);

  useEffect(() => {
    if (open) {
      setFormData(initialState);
    }
  }, [open, initialState]);

  const { mutate: updateFeatures, isPending: isSaving } = $staffApi.useMutation(
    "patch",
    "/api/institution_settings/update_features/",
    {
      onSuccess: () => {
        sendNotification({ type: "success", text: "Products updated successfully." });
        queryClient.invalidateQueries({
          queryKey: ["get", "/api/institution_settings/{setting_name}/"],
        });
        queryClient.invalidateQueries({
          queryKey: ["get", "/api/institution_settings/categories/"],
        });
        onSuccess();
      },
      onError: () => {
        sendNotification({
          type: "negative",
          text: "Failed to save product settings. Please try again.",
        });
      },
    }
  );

  const isAoiobDisabled =
    formData !== null &&
    ((!formData.new_account_opening && !formData.business_account_opening) ||
      (!formData.personal_banking &&
        !formData.business_banking &&
        !formData.online_banking &&
        !formData.mobile_banking));

  useEffect(() => {
    if (isAoiobDisabled && formData?.aoiob) {
      setFormData((prev) => (prev ? { ...prev, aoiob: false } : prev));
    }
  }, [isAoiobDisabled, formData?.aoiob]);

  const isDirty =
    formData !== null &&
    initialState !== null &&
    PRODUCT_KEYS.some((key) => formData[key] !== initialState[key]);

  const handleToggle = (key: ProductKey, value: boolean): void => {
    if (isSaving) return;
    setFormData((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleSave = () => {
    if (!formData || !initialState) return;
    const changedFeatures: Partial<ProductState> = {};
    for (const key of PRODUCT_KEYS) {
      if (formData[key] !== initialState[key]) {
        changedFeatures[key] = formData[key];
      }
    }
    const note = `Updated product settings - ${Object.entries(changedFeatures)
      .map(([key, value]) => `${PRODUCTS[key as ProductKey]}: ${value ? "enabled" : "disabled"}`)
      .join(", ")}`;
    updateFeatures({ body: { features: changedFeatures, note } });
  };

  return (
    <Dialog
      isOpen={open}
      onUserDismiss={onExit}
      title="Edit products"
      headerStyle="bordered"
      footer={
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button kind="plain" label="Cancel" onClick={onExit} />
          </Row.Item>
          <Row.Item shrink>
            <Button label="Save" onClick={handleSave} disabled={!isDirty || isSaving} />
          </Row.Item>
        </Row>
      }
    >
      {isLoading || !formData ? (
        <LoadingSkeleton isLoading />
      ) : (
        <>
          <EditProductSectionHeader icon="bank" title="BANKING" />
          <ContentCard kind="bordered" radiusSize="m" paddingSize="s">
            <ToggleRow
              label={PRODUCTS.personal_banking}
              isActive={formData.personal_banking}
              onChange={(value) => handleToggle("personal_banking", value)}
              paddingSize="s"
            />
            <ToggleRow
              label={PRODUCTS.business_banking}
              isActive={formData.business_banking}
              onChange={(value) => handleToggle("business_banking", value)}
              paddingSize="s"
            />
            <ToggleRow
              label={PRODUCTS.online_banking}
              isActive={formData.online_banking}
              onChange={(value) => handleToggle("online_banking", value)}
              paddingSize="s"
            />
            <ToggleRow
              label={PRODUCTS.mobile_banking}
              isActive={formData.mobile_banking}
              onChange={(value) => handleToggle("mobile_banking", value)}
            />
          </ContentCard>
          <div className="margin--top--xxs">
            <EditProductSectionHeader icon="book-open" title="DEPOSITS" />
          </div>
          <ContentCard kind="bordered" radiusSize="m" paddingSize="s">
            <ToggleRow
              label={PRODUCTS.new_account_opening}
              isActive={formData.new_account_opening}
              onChange={(value) => handleToggle("new_account_opening", value)}
              paddingSize="s"
            />
            <ToggleRow
              label={PRODUCTS.business_account_opening}
              isActive={formData.business_account_opening}
              onChange={(value) => handleToggle("business_account_opening", value)}
              paddingSize="s"
            />
            <ToggleRow
              label={PRODUCTS.staffer_led_ao_consumer}
              isActive={formData.staffer_led_ao_consumer}
              onChange={(value) => handleToggle("staffer_led_ao_consumer", value)}
              paddingSize="s"
            />
            <ToggleRow
              label={PRODUCTS.staffer_led_ao_business}
              isActive={formData.staffer_led_ao_business}
              onChange={(value) => handleToggle("staffer_led_ao_business", value)}
              paddingSize="s"
            />
            <ToggleRow
              label={PRODUCTS.aoiob}
              isActive={formData.aoiob}
              onChange={(value) => handleToggle("aoiob", value)}
              disabled={isAoiobDisabled}
              helperText="Only available when one account opening and one digital banking are turned on"
            />
          </ContentCard>
          <div className="margin--top--xxs">
            <EditProductSectionHeader icon="loan" title="LEND" />
          </div>
          <ContentCard kind="bordered" radiusSize="m" paddingSize="s">
            <ToggleRow
              label={PRODUCTS.lending}
              isActive={formData.lending}
              onChange={(value) => handleToggle("lending", value)}
              paddingSize="none"
            />
          </ContentCard>
        </>
      )}
    </Dialog>
  );
}
